import { useState, useEffect } from 'react';
import MetaTags from '@/components/MetaTags';
import IframeResizer from '@iframe-resizer/react';
import { PAGE_META_DATA } from '@/constants/constants';
import { Info, Loader } from 'lucide-react';
import { DATA_FILTERS, DATA_IFRAMES } from '@/constants/constants';
import DataFilters from '@/components/DataFilters';
import { useParams, useNavigate } from 'react-router-dom';
import { setPageTitle } from '@/lib/appInsights';

function Data() {
  const params = useParams();
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<string>(DATA_FILTERS[0].id);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isMac = window.navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  useEffect(() => {
    const filterSlug = params.filter;

    if (filterSlug && DATA_FILTERS.some((option) => option.id === filterSlug)) {
      setSelectedOption(filterSlug);
    } else if (filterSlug && !DATA_FILTERS.some((option) => option.id === filterSlug)) {
      navigate(`/data/${DATA_FILTERS[0].id}`, { replace: true });
    } else if (!filterSlug) {
      navigate(`/data/${DATA_FILTERS[0].id}`, { replace: true });
    }
  }, [params.filter, navigate]);

  useEffect(() => {
    const selectedOptionName = DATA_FILTERS.find((option) => option.id === params.filter)?.name;
    setPageTitle(PAGE_META_DATA.data.title + (selectedOptionName ? ` - ${selectedOptionName}` : ''));
  }, [params.filter]);

  function handleIframeLoad() {
    setIsLoading(false);
  }

  function handleOptionChange(option: string) {
    setIsLoading(true);
    setSelectedOption(option);
  }

  return (
    <div className='h-full'>
      <MetaTags title={PAGE_META_DATA.data.title} description={PAGE_META_DATA.data.description} />

      <section className='h-full py-8 pb-10 md:py-[28px] md:pb-[46px]'>
        <div className='flex flex-col h-full gap-5 md:gap-2 px-4 mx-auto md:container'>
          <header className='md:pt-4 z-20'>
            <h1 className='font-extrabold text-3xl/8 tracking-tight m-0 mb-[5px]'>Data</h1>
            <p className='text-[#525252] tracking-tight text-[1.125rem]/[1.35rem]'>
              Comprehensive Gas Storage & Capacity Dashboards
            </p>
          </header>

          <main className='relative'>
            <DataFilters disabled={isLoading} selectedOption={selectedOption} setSelectedOption={handleOptionChange} />

            <div className='relative z-10 mt-4 bg-white rounded-md shadow-sm'>
              {selectedOption === 'eurostat-data' && (
                <div className='flex gap-2 items-cetner text-gray-500 text-sm px-4 py-2 mb-4 bg-gray-50 border border-gray-200 rounded-md'>
                  <Info size={13} className='mt-[3px]' /> This dashboard is zoomable. Use{' '}
                  <kbd className='bg-gray-200 px-[7px] rounded-sm'>{isMac ? '⌘' : 'ctrl'}</kbd> + scroll or pinch
                  gestures to zoom in and out.
                </div>
              )}

              <div className='relative min-h-[700px]'>
                <IframeResizer
                  width='100%'
                  license='GPLv3'
                  onLoad={handleIframeLoad}
                  style={{ minHeight: '900px', border: 'none' }}
                  src={DATA_IFRAMES[selectedOption as keyof typeof DATA_IFRAMES]}
                />

                {isLoading && (
                  <div className='absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center'>
                    <Loader className='animate-spin' size={32} />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </section>
    </div>
  );
}

export default Data;
